import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMapsLibrary } from '@vis.gl/react-google-maps';
import shuffle from 'just-shuffle';
import { AddLocation } from 'registration/components/new_pages/OnboardingLocationPersonalized/AddLocation';
import desiredLocationOptions from 'registration/constants/desiredLocationOptions';
import {
  Onboarding,
  OnboardingContext,
  OnboardingTitle,
  TagLocationPinIcon,
} from 'registration/containers/Onboarding';
import {
  ensureDesiredLocationsArray,
  ensureDesiredLocationsTagCompatible,
} from 'registration/helpers/desiredLocations';
import {
  DELIMITER,
  getShouldPreselectLocations,
  getUnbounceParamFromLocalforage,
  UnbounceParam,
} from 'registration/helpers/experiment/xena';
import { locationFromGooglePlace } from 'registration/helpers/talentProfileConversions';

import { Center } from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { Tag, SIZE as TAG_SIZE } from 'shared/components/ihcl/tag';
import { Button } from 'shared/components/ihcl/v2/button';
import { pathToVariant } from 'shared/helpers/activateABTest';
import { doGet } from 'shared/helpers/http';

const NEW_LOCATIONS_COUNT = 10;

const getRecentGradLocations = async () => {
  const cities = await doGet('/talent_locations/recent_grad_cities').then(
    (resp) => resp.cities
  );
  return cities.map(([city, state]) => ({
    id: `${city}, ${state}, United States`,
    label: `${city}, ${state}`,
    city,
    state,
  }));
};

const filteredLocationValues = [
  ...desiredLocationOptions
    .toArray()
    .filter((dl) => Object.prototype.hasOwnProperty.call(dl, 'value')),
];

const desiredLocationStateNameMap = desiredLocationOptions
  .filter((dl) => !Object.prototype.hasOwnProperty.call(dl, 'value'))
  .reduce(
    (stateMap, state) => ({
      ...stateMap,
      [state.section.toUpperCase()]: state.label,
    }),
    {}
  );

const desiredLocationValuesByState: Record<string, LocationTagWithCity[]> = {};
const desiredLocationValues: LocationTagWithCity[] = [];

filteredLocationValues.forEach((dl) => {
  const locationObj = {
    id: `${dl.value.city}, ${dl.value.state}`,
    label: `${dl.value.city}, ${dl.value.state}`,
    city: dl.value.city,
    state: dl.value.state,
  };
  desiredLocationValues.push(locationObj);
  const stateName = desiredLocationStateNameMap[dl.value.state];
  if (!desiredLocationValuesByState[stateName]) {
    desiredLocationValuesByState[stateName] = [];
  }
  desiredLocationValuesByState[stateName].push(locationObj);
});

desiredLocationValues.sort((a, b) => a.label.localeCompare(b.label));

type LocationTag = {
  id: string;
  label: string;
  state: string;
  zipCode?: string;
};

type LocationTagsProps = {
  locations: LocationTag[];
  removeTag: (tag: LocationTag) => void;
};

const LocationTags = ({ locations, removeTag }: LocationTagsProps) => (
  <Center>
    {locations.map((location) => (
      <Tag
        $fadeIn
        $fullWidth
        key={location.id}
        size={TAG_SIZE.medium}
        onActionClick={() => removeTag(location)}
      >
        {location.zipCode && <TagLocationPinIcon />}
        {location.label}
      </Tag>
    ))}
  </Center>
);

type LocationTagWithCity = LocationTag & {
  city: string;
};

type NewLocationTagsProps = {
  locations: LocationTagWithCity[];
  moveTag: (tag: LocationTagWithCity) => void;
};

const NewLocationTags = ({ locations, moveTag }: NewLocationTagsProps) => (
  <Center>
    {locations.map((location) => (
      <Tag
        key={location.id}
        closeable={false}
        kind="primary"
        size={TAG_SIZE.medium}
        onClick={() => moveTag(location)}
        variant="solid"
      >
        {location.label}
      </Tag>
    ))}
  </Center>
);

type NewLocationsProps = {
  locations: {
    newLocations: LocationTagWithCity[];
    unfilteredCount?: number;
  };
  moveTag: (tag: LocationTagWithCity) => void;
  replaceLocations: () => void;
};

const NewLocations = ({
  locations,
  moveTag,
  replaceLocations,
}: NewLocationsProps) =>
  locations.newLocations.length > 0 && (
    <Center>
      <span style={{ fontWeight: 900 }}>Popular cities with nurses</span>
      <p>Tap to add cities you are interested in.</p>
      <NewLocationTags locations={locations.newLocations} moveTag={moveTag} />
      {locations.unfilteredCount > NEW_LOCATIONS_COUNT && (
        <Button variant="minimal" onClick={replaceLocations}>
          Show different cities
        </Button>
      )}
    </Center>
  );

const SelectedLocations = styled('div', {});
const getNewLocations = (
  locationsToExclude,
  locationCount = NEW_LOCATIONS_COUNT,
  locationValues = desiredLocationValues
) => {
  const shuffledLocations = shuffle(locationValues);
  if (locationsToExclude && locationsToExclude.length > 0) {
    const filterdLocations = shuffledLocations.filter(
      (location) =>
        !locationsToExclude.some((lte) => {
          if (!lte) {
            return false;
          }
          return lte.id === location.id;
        })
    );
    const unfilteredCount = filterdLocations.length;
    return {
      newLocations: filterdLocations.splice(0, locationCount),
      unfilteredCount,
    };
  }

  const unfilteredCount = shuffledLocations.length;
  return {
    newLocations: shuffledLocations.splice(0, locationCount),
    unfilteredCount,
  };
};

type OnboardingLocationProps = {
  nextAction?: string;
  previousAction?: string;
  stepNumber: number;
};

const OnboardingLocation = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
}: OnboardingLocationProps) => {
  const { onboardingBasePath, onboardingData, setOnboardingData } =
    useContext(OnboardingContext);
  const { desiredLocations, recent_grad: recentGrad } = onboardingData;

  // HACK: Force to only DesiredLocations. Place have no id and do not work with
  //       with existing code. This is maybe most egregious ambigious typing.
  const selectedLocations =
    ensureDesiredLocationsTagCompatible(desiredLocations);

  const emptySelectedLocations =
    !selectedLocations || (selectedLocations && selectedLocations.length === 0);
  const [recentGradLocations, setRecentGradLocations] = useState<
    | boolean
    | {
        city: any;
        id: any;
        label: string;
        state: any;
      }[]
  >(null);
  const hasRecentGradLocations =
    recentGradLocations &&
    recentGradLocations instanceof Array &&
    recentGradLocations.length > 0;
  const [newLocations, setNewLocations] = useState<
    NewLocationsProps['locations']
  >(
    getNewLocations(
      selectedLocations,
      NEW_LOCATIONS_COUNT,
      hasRecentGradLocations ? recentGradLocations : desiredLocationValues
    )
  );
  const [paramLocations, setParamLocations] = useState(null);

  const addTag = useCallback(
    (tagObj) => {
      setOnboardingData((data) => {
        if (
          data.desiredLocations.every((location) => location.id !== tagObj.id)
          // using data.desiredLocations instead of selectedLocations here to
          // eliminate extra dependency and excessive firing of below effect
        ) {
          return {
            ...data,
            desiredLocations: [
              ...ensureDesiredLocationsArray(data.desiredLocations),
              tagObj,
            ],
          };
        }

        return data;
      });
    },
    [setOnboardingData]
  );

  const [shouldPreselectOtherLocations, setShouldPreselectOtherLocations] =
    useState(false);

  const variant = pathToVariant(onboardingBasePath);

  useEffect(() => {
    getShouldPreselectLocations(variant).then((value) =>
      setShouldPreselectOtherLocations(value)
    );
  }, [variant]);

  useEffect(() => {
    if (shouldPreselectOtherLocations) {
      // also get any locations we saved from unbound query param
      const getParamLocation = async () => {
        const paramValue = await getUnbounceParamFromLocalforage(
          UnbounceParam.desired_location
        );

        setParamLocations(paramValue);
      };

      getParamLocation();
    }
  });

  const placesLib = useMapsLibrary('places');
  const autocompleteService = useMemo(
    () => placesLib && new placesLib.AutocompleteService(),
    [placesLib]
  );

  const getResultsFromGoogle = async (input) => {
    const resp = await autocompleteService?.getPlacePredictions({
      input,
      componentRestrictions: { country: 'us' },
      types: ['(cities)'],
    });

    return resp?.predictions;
  };

  useEffect(() => {
    const addTagsFromGoogleMatches = async () => {
      paramLocations?.split(DELIMITER).forEach(async (paramLocation) => {
        if (paramLocation.trim()) {
          const results = await getResultsFromGoogle(paramLocation);

          if (results?.length > 0) {
            const locationValues = results.map(locationFromGooglePlace) as {
              city: any;
              id: any;
              label: string;
              state: any;
            }[];
            const match = locationValues.find(
              (locationValue) =>
                locationValue?.label.replace(/ +/g, '').toLowerCase() ===
                paramLocation.replace(/ +/g, '').toLowerCase()
            );
            if (match) addTag(match);
          }
        }
      });
    };

    addTagsFromGoogleMatches();
  }, [paramLocations, addTag, autocompleteService]);

  const replaceLocations = () => {
    const locationsToAppend = getNewLocations(
      selectedLocations,
      NEW_LOCATIONS_COUNT,
      hasRecentGradLocations ? recentGradLocations : desiredLocationValues
    );
    setNewLocations(locationsToAppend);
  };
  const removeTag = ({ id }) => {
    const filteredTags = selectedLocations.filter((tag) => tag.id !== id);
    setOnboardingData({
      ...onboardingData,
      desiredLocations: filteredTags,
    });
  };
  const replaceNewLocationTag = (oldLocation) => {
    let unfilteredCount;
    const locationValues = hasRecentGradLocations
      ? recentGradLocations
      : desiredLocationValues;
    const updatedNewLocations = newLocations.newLocations
      .map((location) => {
        if (location.id === oldLocation.id) {
          const {
            newLocations: newLocationsArr,
            unfilteredCount: newUnfilteredCount,
          } = getNewLocations(
            [...selectedLocations, ...newLocations.newLocations],
            1,
            locationValues
          );
          unfilteredCount = newUnfilteredCount;
          const newLocation = newLocationsArr[0];
          if (newLocation) {
            return newLocation;
          }
          return null;
        }
        return location;
      })
      .filter((location) => !!location);
    setNewLocations({
      newLocations: updatedNewLocations,
      unfilteredCount,
    });
  };
  const moveTag = (tag) => {
    replaceNewLocationTag(tag);
    addTag(tag);
  };

  if (recentGrad && recentGradLocations === null) {
    setRecentGradLocations(false);
    getRecentGradLocations().then((resp) => {
      if (resp && Array.isArray(resp)) {
        if (resp.length > 0) {
          setNewLocations(
            getNewLocations(selectedLocations, NEW_LOCATIONS_COUNT, resp)
          );
        }
        setRecentGradLocations(resp);
      }
    });
  }

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={emptySelectedLocations}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['desiredLocations']}
      stepName="Additional locations"
    >
      <OnboardingTitle>Where would you like to work?</OnboardingTitle>
      <SelectedLocations>
        {selectedLocations && (
          <LocationTags locations={selectedLocations} removeTag={removeTag} />
        )}
        {!recentGrad && (
          <AddLocation
            addTag={addTag}
            defaultOptions={desiredLocationValuesByState}
            getResultsFromGoogle={getResultsFromGoogle}
            recentGrad={recentGrad}
          />
        )}
      </SelectedLocations>
      {newLocations?.newLocations &&
        !(recentGrad && recentGradLocations === false) && (
          <NewLocations
            locations={newLocations}
            moveTag={moveTag}
            replaceLocations={replaceLocations}
          />
        )}
      {recentGrad && (
        <AddLocation
          addTag={addTag}
          defaultOptions={desiredLocationValuesByState}
          getResultsFromGoogle={getResultsFromGoogle}
          recentGrad={recentGrad}
        />
      )}
    </Onboarding>
  );
};

export default OnboardingLocation;
